import React, { useEffect, useState } from "react";

import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ShareIcon from "@mui/icons-material/Share";
import WarningIcon from "@mui/icons-material/Warning";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import "css/spinner.css";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { getDestinationIcon } from "helpers/destinations/getDestinationIcon";
import { getDestinationDocumentLink } from "helpers/documentation/getDocumentationLinks";

import EmptyState from "components/atoms/EmptyState";
import { ModalName } from "components/organisms/ModalRoot/ModalRoot.layout";

import PageContainer from "../../components/atoms/PageContainer";
import { getAllDestinationsRedux } from "../../redux/slices/destinations/destinations.asyncActions";
import { openModal } from "../../redux/slices/modal/modal.slice";
import {
    deleteLastAddedDest,
    deleteLastAddedSource,
} from "../../redux/slices/wizardSourceDestination/wizardSourceDestination.slice";
import {
    CardHeaderAction,
    CardHeaderLogo,
    CardHeaderTitle,
    LogoIcon,
    StyledBottomCardContent,
    StyledCardContent,
    StyledCardHeader,
    StyledDestinationCard,
} from "../shared/CardStyles";
import DeleteDestinationDialog from "../Trackers/TrackerDetails/DestinationModal/components/DeleteDestinationDialog";
import DestinationModal from "../Trackers/TrackerDetails/DestinationModal/DestinationModal";
import "./destinations.css";

const Destinations = () => {
    const location = useLocation();
    useEffect(() => {
        return () => {
            dispatch(deleteLastAddedDest());
            dispatch(deleteLastAddedSource());
        };
    }, []);

    const handleShow = () =>
        dispatch(openModal({ modalName: ModalName.CREATE_DESTINATION }));

    const destinations = useSelector(
        (state) => state.allDestinationsState.getAllDestinations.destinations,
    );
    const language = useSelector((state) => state.languagesState.languages);
    const loading = useSelector(
        (state) => state.allDestinationsState.getAllDestinations.status,
    );
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getAllDestinationsRedux());
        location.search && location.search.length > 0 && handleShow();
    }, [dispatch]);

    const [destinationModalOpen, setDestinationModalOpen] = useState(false);
    const [destinationModalItem, setDestinationModalItem] = useState({});
    const [destinationModalInitialTab, setDestinationModalInitialTab] =
        useState(null);

    const handleOpenDestinationModal = (item, initialTab = null) => {
        setDestinationModalOpen(true);
        setDestinationModalItem(item);
        setDestinationModalInitialTab(initialTab);
    };

    const destinationModalOnHide = () => {
        setDestinationModalOpen(false);
        setDestinationModalItem({});
        setDestinationModalInitialTab(null);
    };

    // Always read the latest destination from Redux so the modal reflects reconnect changes.
    const activeDestinationModalItem =
        destinations?.find((x) => x.id === destinationModalItem?.id) ||
        destinationModalItem;

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteDialogItem, setDeleteDialogItem] = useState({});

    const openDeleteDialog = (item) => {
        setDeleteDialogOpen(true);
        setDeleteDialogItem(item);
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setDeleteDialogItem({});
    };

    const DestinationErrorTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: "#000000",
            color: "#000",
            maxWidth: 220,
            fontSize: "12px",
            borderRadius: "10px",
            fontFamily: '"Nunito", serif',
            padding: "15px",
            boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
        },
        [`& .${tooltipClasses.tooltip} p`]: {
            fontWeight: "bold",
            fontFamily: '"Nunito", serif',
            color: "white",
            fontSize: "14px",
        },
        [`& .${tooltipClasses.tooltip} span`]: {
            fontWeight: "300",
            fontFamily: '"Nunito", serif',
            color: "white",
            fontSize: "12px",
        },
    }));

    const openDocumentNewTab = (event, url) => {
        event.preventDefault();
        event.stopPropagation();
        openInNewTab(url);
        return false;
    };

    const openInNewTab = (url) => {
        window.open(url, "_blank", "noreferrer");
    };

    return (
        <PageContainer
            title={language?.sidebar?.destinations ?? "Destinations"}
            subtitle={
                language?.destinations?.destinations_desc ??
                "On this page, you can view destinations, and create a new one by using the button."
            }
            additionalButtons={
                <Button
                    data-cy="destinations-add"
                    onClick={handleShow}
                    color="primary"
                    variant="contained"
                >
                    {language?.destinations?.add_destination ??
                        "Add Destination"}
                </Button>
            }
            loading={loading === "idle" || loading === "loading"}
            variant="list"
        >
            {!destinations?.length ? (
                <EmptyState
                    icon={<ShareIcon />}
                    title={
                        language?.destinations?.no_destinations_yet ??
                        "No destinations yet"
                    }
                    description={
                        language?.destinations?.no_destinations_yet_desc ??
                        "Add your first destination to start sending data from your trackers."
                    }
                />
            ) : (
                <Grid
                    container
                    rowSpacing={3}
                    columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                >
                    {destinations?.map((item, index) => (
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 3,
                            }}
                        >
                            <StyledDestinationCard
                                data-cy="destination-card"
                                data-destination-id={item.id}
                                onClick={() => handleOpenDestinationModal(item)}
                                isError={item.status === 0}
                            >
                                <StyledCardHeader>
                                    <CardHeaderLogo>
                                        <LogoIcon
                                            src={getDestinationIcon(
                                                destinations,
                                                item?.destination_type?.id,
                                            )}
                                        />
                                    </CardHeaderLogo>
                                    <CardHeaderTitle
                                        sx={{
                                            fontSize: 18,
                                            fontWeight: "700",
                                            fontFamily: "Nunito, sans-serif",
                                            color:
                                                item?.status === 0
                                                    ? "red"
                                                    : "black",
                                        }}
                                    >
                                        {item?.destination_type?.name}
                                        {item?.status === 0 && (
                                            <DestinationErrorTooltip
                                                placement="top"
                                                style={{ marginLeft: "10px" }}
                                                title={
                                                    <React.Fragment>
                                                        <Typography color="white">
                                                            {language
                                                                ?.tracker_detail
                                                                ?.error ??
                                                                "Error"}
                                                        </Typography>
                                                        <span>
                                                            {language
                                                                ?.tracker_detail
                                                                ?.destination_error_tooltip_message ??
                                                                "Since the Access Token is expired, you must reconnect the destination."}
                                                        </span>
                                                    </React.Fragment>
                                                }
                                            >
                                                <WarningIcon></WarningIcon>
                                            </DestinationErrorTooltip>
                                        )}
                                    </CardHeaderTitle>
                                    {getDestinationDocumentLink(
                                        item?.destination_type?.code,
                                    ) !== "" && (
                                        <CardHeaderAction>
                                            <a
                                                data-cy="destination-documentation"
                                                href="javascript:;"
                                                onClick={(e) =>
                                                    openDocumentNewTab(
                                                        e,
                                                        getDestinationDocumentLink(
                                                            item
                                                                ?.destination_type
                                                                ?.code,
                                                        ),
                                                    )
                                                }
                                                className="document-view-link width-auto"
                                                style={{ color: "#ed6c02" }}
                                            >
                                                <MenuBookOutlinedIcon />
                                            </a>
                                        </CardHeaderAction>
                                    )}
                                </StyledCardHeader>
                                <StyledCardContent>
                                    <span
                                        style={{
                                            display: "block",
                                            fontSize: "14px",
                                            color: "black",
                                            fontWeight: "500",
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: "block",
                                                fontSize: "12px",
                                                color: "#ABABAB",
                                                fontWeight: "400",
                                            }}
                                        >
                                            {language?.tracker_detail?.name ??
                                                "Name"}
                                        </span>
                                        <div
                                            style={{
                                                wordBreak: "break-word",
                                                lineClamp: 2,
                                                display: "-webkit-box",
                                                "-webkit-line-clamp": 2,
                                                "-webkit-box-orient":
                                                    "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {item?.name}
                                        </div>
                                    </span>
                                </StyledCardContent>
                                {item?.trackers && item?.trackers.length > 0 ? (
                                    <StyledBottomCardContent>
                                        <div className="in-use-area">
                                            {language?.destinations?.in_use ??
                                                "In Use"}
                                        </div>
                                    </StyledBottomCardContent>
                                ) : (
                                    <StyledBottomCardContent>
                                        <div className="not-in-use-area">
                                            {language?.destinations
                                                ?.not_in_use ?? "Not In Use"}
                                        </div>
                                    </StyledBottomCardContent>
                                )}
                                {item?.status === 0 && (
                                    <StyledBottomCardContent>
                                        <Button
                                            data-cy="destination-reconnect"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenDestinationModal(
                                                    item,
                                                    "reconnect",
                                                );
                                            }}
                                            style={{
                                                minWidth: "unset !important",
                                            }}
                                            color="primary"
                                            variant="contained"
                                        >
                                            {language?.tracker_detail
                                                ?.reconnect ?? "Reconnect"}
                                        </Button>
                                        <Tooltip
                                            title={
                                                item?.trackers?.length > 0
                                                    ? (language?.destinations
                                                          ?.cannot_delete_in_use ??
                                                      "This destination is in use by a tracker and cannot be deleted.")
                                                    : ""
                                            }
                                            placement="top"
                                        >
                                            <span>
                                                <Button
                                                    data-cy="destination-remove"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDeleteDialog(item);
                                                    }}
                                                    color="error"
                                                    variant="outlined"
                                                    disabled={
                                                        item?.trackers?.length >
                                                        0
                                                    }
                                                    style={{
                                                        minWidth:
                                                            "unset !important",
                                                    }}
                                                >
                                                    {language?.tracker_detail
                                                        ?.remove ?? "Remove"}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </StyledBottomCardContent>
                                )}
                            </StyledDestinationCard>
                        </Grid>
                    ))}
                </Grid>
            )}
            <DestinationModal
                isOpen={destinationModalOpen}
                destination={activeDestinationModalItem}
                language={language}
                onClose={destinationModalOnHide}
                onRefresh={() => dispatch(getAllDestinationsRedux())}
                mode="standalone"
                initialTab={destinationModalInitialTab}
            />
            <DeleteDestinationDialog
                open={deleteDialogOpen}
                onClose={closeDeleteDialog}
                destination={deleteDialogItem}
                language={language}
                mode="standalone"
                onDeleteSuccess={closeDeleteDialog}
                onRefresh={() => dispatch(getAllDestinationsRedux())}
            />
        </PageContainer>
    );
};

export default Destinations;
